import { observer } from 'mobx-react-lite'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ClickableList,
  ClickableListItem,
  EmptyState,
  RightActionButton
} from '@cdab/scania/qpr/components/atoms'
import type { FileData } from '@cdab/scania/qpr/schema'

import { TdsBlock, TdsDivider, TdsIcon } from '@scania/tegel-react'
import { StyledHeader, Title } from './attachment-list.styles'

type FileType = 'images' | 'other' | 'all'

interface AttachmentListProps {
  files?: FileData[]
  onDeleteAttachment?: (id: FileData['id']) => void
  disableDelete: boolean
  fileType?: FileType
  title?: string
  hideUploadProgress?: boolean
  hideUploadPrompt?: boolean
  modeVariant?: 'primary' | 'secondary'
}

type FileRowProps = {
  file: FileData
  onDelete?: (id: FileData['id']) => void
  disableDelete: boolean
  hideUploadProgress?: boolean
}

const FileRow = observer(function FileRow({
  file,
  onDelete,
  disableDelete,
  ...props
}: FileRowProps) {
  const { fileName, id, url, isImage } = file
  const previewImage = true
  const tooltipId = 'id-' + id.toString()

  const onDeleteClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      const fileId = e.currentTarget.dataset['fileId']
      if (fileId && onDelete) {
        onDelete(fileId)
      }
    },
    [onDelete]
  )

  return (
    <ClickableListItem
      breakTitleWords
      breakDescriptionWords
      key={id}
      title={fileName}
      url={url}
      openInNewTab
      thumbnail={
        previewImage && isImage
          ? {
              src: url,
              alt: 'User uploaded'
            }
          : undefined
      }
      rightItem={
        !disableDelete && (
          <RightActionButton
            id={tooltipId}
            icon={<TdsIcon name='cross' size='16px' />}
            data-file-id={id}
            onClick={onDeleteClick}
          />
        )
      }
    >
      {!props.hideUploadProgress && file.uploadProgress !== 1 && (
        <progress max={1} value={file.uploadProgress}>
          {Math.round(file.uploadProgress * 100)}%
        </progress>
      )}
    </ClickableListItem>
  )
})

function filterFiles(files: FileData[], fileType: FileType): FileData[] {
  if (fileType === 'images') {
    return files.filter((file: FileData) => file.isImage)
  }

  if (fileType === 'other') {
    return files.filter((file: FileData) => !file.isImage)
  }

  return files
}

export function AttachmentList({
  files = [],
  onDeleteAttachment,
  disableDelete,
  fileType = 'all',
  title,
  hideUploadProgress,
  modeVariant = 'primary',
  ...props
}: AttachmentListProps): JSX.Element | null {
  const { t } = useTranslation('audit')

  const filteredFiles = filterFiles(files, fileType)

  if (filteredFiles.length === 0) {
    return (
      <TdsBlock>
        <StyledHeader>{title}</StyledHeader>
        <TdsDivider variant='discrete' />
        <EmptyState
          title={t('attachments.empty-state.title')}
          description={
            props.hideUploadPrompt
              ? undefined
              : t('attachments.empty-state.description')
          }
        />
      </TdsBlock>
    )
  }

  return (
    <>
      {title && <Title>{title}</Title>}
      <div className={`tds-mode-variant-${modeVariant}`} {...props}>
        <div className={`tds-mode-variant-${modeVariant}`}>
          <ClickableList>
            {filteredFiles.map(file => (
              <FileRow
                key={file.id}
                file={file}
                onDelete={onDeleteAttachment}
                disableDelete={disableDelete}
                hideUploadProgress={hideUploadProgress}
              />
            ))}
          </ClickableList>
        </div>
      </div>
    </>
  )
}
